import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useForm, Link, router } from '@inertiajs/react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/Layouts/AppLayout';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { Toaster, toast } from 'sonner';
import { useHotkeys } from 'react-hotkeys-hook';

// Service type configurations
const SERVICE_TYPES = {
    hotel: { icon: 'bi-building', color: '#f97316', label: 'Hotel', hasNights: true },
    transfer: { icon: 'bi-car-front', color: '#22c55e', label: 'Transfer', hasVehicle: true },
    sightseeing: { icon: 'bi-camera', color: '#3b82f6', label: 'Sightseeing', hasTickets: true },
    meal: { icon: 'bi-cup-hot', color: '#ec4899', label: 'Meal' },
    visa: { icon: 'bi-passport', color: '#8b5cf6', label: 'Visa' },
    flight: { icon: 'bi-airplane', color: '#6366f1', label: 'Flight', hasFlight: true },
    cruise: { icon: 'bi-water', color: '#06b6d4', label: 'Cruise', hasNights: true },
    insurance: { icon: 'bi-shield-check', color: '#14b8a6', label: 'Insurance' },
    guide: { icon: 'bi-person-badge', color: '#f59e0b', label: 'Guide' },
    other: { icon: 'bi-three-dots', color: '#64748b', label: 'Other' },
};

const ROOM_TYPES = [
    { key: 'single', label: 'SGL', desc: '1 Adult' },
    { key: 'double', label: 'DBL', desc: '2 Adults' },
    { key: 'triple', label: 'TRPL', desc: '3 Adults' },
    { key: 'quad', label: 'QUAD', desc: '4 Adults' },
    { key: 'extra_bed', label: 'ExB', desc: 'Extra Bed' },
    { key: 'cwb', label: 'CWB', desc: 'Child+Bed' },
    { key: 'cnb', label: 'CNB', desc: 'Child No Bed' },
    { key: 'infant', label: 'INF', desc: 'Infant' },
];

const MEAL_PLANS = {
    ep: 'Room Only (EP)',
    cp: 'Breakfast (CP)',
    map: 'Breakfast + Dinner (MAP)',
    ap: 'All Meals (AP)',
    ai: 'All Inclusive (AI)',
};

// Sortable Service Item Component
function SortableServiceItem({ item, onEdit, onDelete, currency, isDragging }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging: isSorting,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isSorting ? 0.5 : 1,
    };

    const serviceConfig = SERVICE_TYPES[item.service_type] || SERVICE_TYPES.other;

    return (
        <motion.div
            ref={setNodeRef}
            style={style}
            className={`service-item ${isSorting ? 'dragging' : ''}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            layout
        >
            <div className="service-drag-handle" {...attributes} {...listeners}>
                <i className="bi bi-grip-vertical"></i>
            </div>

            <div className="service-icon" style={{ backgroundColor: serviceConfig.color }}>
                <i className={`bi ${serviceConfig.icon}`}></i>
            </div>

            <div className="service-content">
                <div className="service-header">
                    <h6 className="service-name">{item.name || 'Untitled Service'}</h6>
                    <span className="service-badge" style={{ backgroundColor: `${serviceConfig.color}20`, color: serviceConfig.color }}>
                        {serviceConfig.label}
                    </span>
                    {item.is_optional && (
                        <span className="badge bg-warning-subtle text-warning">Optional</span>
                    )}
                </div>
                <div className="service-meta">
                    {item.description && <span className="text-muted">{item.description}</span>}
                    {item.supplier?.name && (
                        <span className="service-supplier">
                            <i className="bi bi-shop me-1"></i>
                            {item.supplier.name}
                        </span>
                    )}
                </div>
                <div className="service-pricing">
                    <span className="service-cost">
                        Cost: {currency.symbol}{parseFloat(item.total_cost || 0).toLocaleString('en-IN')}
                    </span>
                    <span className="service-sell">
                        Sell: {currency.symbol}{parseFloat(item.total_selling || 0).toLocaleString('en-IN')}
                    </span>
                </div>
            </div>

            <div className="service-actions">
                <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(item)}>
                    <i className="bi bi-pencil"></i>
                </button>
                <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(item)}>
                    <i className="bi bi-trash"></i>
                </button>
            </div>
        </motion.div>
    );
}

// Day Card Component
function DayCard({ day, dayNumber, items, onAddService, onEditService, onDeleteService, currency, isActive, onClick }) {
    const dayTotal = items.reduce((sum, item) => sum + parseFloat(item.total_selling || 0), 0);
    const itemIds = items.map(item => item.id);

    return (
        <div className={`day-card ${isActive ? 'active' : ''}`} onClick={onClick}>
            <div className="day-header">
                <div className="day-number">
                    <span className="day-num">Day {dayNumber}</span>
                    <span className="day-date">{day.date || 'TBD'}</span>
                </div>
                <div className="day-stats">
                    <span className="day-items">{items.length} services</span>
                    <span className="day-total">{currency.symbol}{dayTotal.toLocaleString('en-IN')}</span>
                </div>
            </div>

            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <div className="day-services">
                    <AnimatePresence mode="popLayout">
                        {items.map(item => (
                            <SortableServiceItem
                                key={item.id}
                                item={item}
                                onEdit={onEditService}
                                onDelete={onDeleteService}
                                currency={currency}
                            />
                        ))}
                    </AnimatePresence>

                    {items.length === 0 && (
                        <div className="day-empty">
                            <i className="bi bi-calendar-plus"></i>
                            <p>No services for this day</p>
                        </div>
                    )}
                </div>
            </SortableContext>

            <button className="day-add-btn" onClick={(e) => { e.stopPropagation(); onAddService(dayNumber); }}>
                <i className="bi bi-plus-lg me-2"></i>
                Add Service
            </button>
        </div>
    );
}

export default function Edit({ query, quotation, suppliers, destinations, currencies, markupTypes }) {
    const [activeDay, setActiveDay] = useState(1);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showPreview, setShowPreview] = useState(true);
    const [viewMode, setViewMode] = useState('timeline'); // timeline, grid, list
    const [activeId, setActiveId] = useState(null);
    const [showCharts, setShowCharts] = useState(false);

    const roomConfig = quotation.room_config || {};
    const currency = currencies.find(c => c.id === quotation.currency_id) || { symbol: '₹', code: 'INR' };

    // DnD Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Organize items by day
    const itemsByDay = useMemo(() => {
        const grouped = {};
        const nights = quotation.nights || 1;

        for (let i = 1; i <= nights + 1; i++) {
            grouped[i] = [];
        }

        (quotation.items || []).forEach(item => {
            const day = item.day_number || 1;
            if (!grouped[day]) grouped[day] = [];
            grouped[day].push(item);
        });

        // Sort items within each day
        Object.keys(grouped).forEach(day => {
            grouped[day].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });

        return grouped;
    }, [quotation.items, quotation.nights]);

    // Calculate totals
    const totals = useMemo(() => {
        const items = quotation.items || [];
        const totalCost = items.reduce((sum, item) => sum + parseFloat(item.total_cost || 0), 0);
        const totalSelling = items.reduce((sum, item) => sum + parseFloat(item.total_selling || 0), 0);
        const profit = totalSelling - totalCost;
        const profitMargin = totalCost > 0 ? (profit / totalCost) * 100 : 0;

        return { totalCost, totalSelling, profit, profitMargin };
    }, [quotation.items]);

    // Chart data for cost breakdown
    const chartData = useMemo(() => {
        const byType = {};
        (quotation.items || []).forEach(item => {
            const type = item.service_type || 'other';
            if (!byType[type]) byType[type] = 0;
            byType[type] += parseFloat(item.total_cost || 0);
        });

        return Object.entries(byType).map(([type, value]) => ({
            name: SERVICE_TYPES[type]?.label || type,
            value,
            color: SERVICE_TYPES[type]?.color || '#64748b',
        }));
    }, [quotation.items]);

    // Day-wise chart data
    const dayWiseData = useMemo(() => {
        return Object.entries(itemsByDay).map(([day, items]) => ({
            day: `Day ${day}`,
            cost: items.reduce((sum, item) => sum + parseFloat(item.total_cost || 0), 0),
            selling: items.reduce((sum, item) => sum + parseFloat(item.total_selling || 0), 0),
        }));
    }, [itemsByDay]);

    // Supplier options for react-select
    const supplierOptions = useMemo(() => {
        return suppliers.map(s => ({
            value: s.id,
            label: s.name,
            serviceTypes: s.service_types || [],
        }));
    }, [suppliers]);

    // Destination options for react-select
    const destinationOptions = useMemo(() => {
        return destinations.map(d => ({
            value: d.id,
            label: d.name,
            country: d.country?.name,
        }));
    }, [destinations]);

    // Keyboard shortcuts
    useHotkeys('ctrl+s, cmd+s', (e) => {
        e.preventDefault();
        toast.success('Quotation auto-saved!');
    }, { enableOnFormTags: true });

    useHotkeys('ctrl+n, cmd+n', (e) => {
        e.preventDefault();
        handleAddService(activeDay);
    }, { enableOnFormTags: false });

    useHotkeys('escape', () => {
        setShowServiceModal(false);
        setEditingItem(null);
    });

    useHotkeys('1,2,3,4,5,6,7,8,9', (e) => {
        const day = parseInt(e.key);
        if (day <= Object.keys(itemsByDay).length) {
            setActiveDay(day);
            toast.info(`Switched to Day ${day}`);
        }
    }, { enableOnFormTags: false });

    // Drag handlers
    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (active.id !== over?.id) {
            // Find source and destination
            const allItems = quotation.items || [];
            const activeItem = allItems.find(i => i.id === active.id);
            const overItem = allItems.find(i => i.id === over?.id);

            if (activeItem && overItem) {
                // Update sort order
                router.post(`/queries/${query.id}/quotations/${quotation.id}/reorder`, {
                    item_id: activeItem.id,
                    target_day: overItem.day_number,
                    target_position: overItem.sort_order,
                }, {
                    preserveScroll: true,
                    onSuccess: () => toast.success('Service reordered'),
                });
            }
        }
    };

    const handleAddService = (dayNumber) => {
        setEditingItem(null);
        setActiveDay(dayNumber);
        setShowServiceModal(true);
    };

    const handleEditService = (item) => {
        setEditingItem(item);
        setShowServiceModal(true);
    };

    const handleDeleteService = (item) => {
        if (confirm('Delete this service?')) {
            router.delete(`/queries/${query.id}/quotations/${quotation.id}/items/${item.id}`, {
                preserveScroll: true,
                onSuccess: () => toast.success('Service deleted'),
            });
        }
    };

    // Generate days array
    const days = Object.keys(itemsByDay).map(d => ({
        number: parseInt(d),
        date: quotation.travel_date ? new Date(new Date(quotation.travel_date).getTime() + (parseInt(d) - 1) * 86400000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : null,
    }));

    return (
        <AppLayout title={`Edit Quotation - ${quotation.quotation_number}`}>
            <Toaster position="top-right" richColors />

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <div className="qb-edit-page">
                    {/* Header */}
                    <div className="qb-header">
                        <div className="qb-header-left">
                            <Link href={`/queries/${query.id}`} className="qb-back-btn">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <div>
                                <h1 className="qb-title">{quotation.title}</h1>
                                <div className="qb-subtitle">
                                    <span className="badge bg-primary-subtle text-primary me-2">
                                        {quotation.quotation_number}
                                    </span>
                                    <span>{query.client?.name || query.guest_name}</span>
                                    <span className="mx-2">•</span>
                                    <span>{quotation.nights}N/{(quotation.nights || 0) + 1}D</span>
                                    <span className="mx-2">•</span>
                                    <span>{quotation.adults}A + {quotation.children || 0}C + {quotation.infants || 0}I</span>
                                </div>
                            </div>
                        </div>

                        <div className="qb-header-actions">
                            <div className="btn-group me-2">
                                <button
                                    className={`btn btn-sm ${viewMode === 'timeline' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('timeline')}
                                >
                                    <i className="bi bi-view-list"></i>
                                </button>
                                <button
                                    className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <i className="bi bi-grid"></i>
                                </button>
                            </div>

                            <button
                                className={`btn btn-sm ${showCharts ? 'btn-info' : 'btn-outline-info'} me-2`}
                                onClick={() => setShowCharts(!showCharts)}
                            >
                                <i className="bi bi-pie-chart me-1"></i>
                                Analytics
                            </button>

                            <button
                                className={`btn btn-sm ${showPreview ? 'btn-secondary' : 'btn-outline-secondary'} me-2`}
                                onClick={() => setShowPreview(!showPreview)}
                            >
                                <i className={`bi bi-${showPreview ? 'eye-slash' : 'eye'} me-1`}></i>
                                {showPreview ? 'Hide' : 'Show'} Summary
                            </button>

                            <Link href={`/queries/${query.id}/quotations/${quotation.id}/preview`} className="btn btn-sm btn-outline-primary me-2">
                                <i className="bi bi-file-pdf me-1"></i>
                                Preview PDF
                            </Link>

                            <Link href={`/queries/${query.id}`} className="btn btn-sm btn-success">
                                <i className="bi bi-check-lg me-1"></i>
                                Done
                            </Link>
                        </div>
                    </div>

                    {/* Keyboard shortcuts hint */}
                    <div className="qb-shortcuts-bar">
                        <span><kbd>Ctrl</kbd>+<kbd>N</kbd> Add Service</span>
                        <span><kbd>1-9</kbd> Switch Day</span>
                        <span><kbd>Esc</kbd> Close Modal</span>
                    </div>

                    {/* Analytics Charts */}
                    <AnimatePresence>
                        {showCharts && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="qb-charts-section"
                            >
                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="chart-card">
                                            <h6>Cost by Service Type</h6>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <PieChart>
                                                    <Pie
                                                        data={chartData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={40}
                                                        outerRadius={80}
                                                        paddingAngle={2}
                                                        dataKey="value"
                                                    >
                                                        {chartData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip formatter={(value) => `${currency.symbol}${value.toLocaleString('en-IN')}`} />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="chart-card">
                                            <h6>Day-wise Cost vs Selling</h6>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={dayWiseData}>
                                                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                                                    <YAxis tick={{ fontSize: 11 }} />
                                                    <Tooltip formatter={(value) => `${currency.symbol}${value.toLocaleString('en-IN')}`} />
                                                    <Bar dataKey="cost" name="Cost" fill="#94a3b8" />
                                                    <Bar dataKey="selling" name="Selling" fill="#6366f1" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Main Content */}
                    <div className="qb-content">
                        <div className="row g-4">
                            {/* Days Navigation */}
                            <div className="col-auto">
                                <div className="qb-days-nav">
                                    <h6 className="qb-days-title">
                                        <i className="bi bi-calendar3 me-2"></i>
                                        Itinerary
                                    </h6>
                                    <div className="qb-days-list">
                                        {days.map(day => {
                                            const dayItems = itemsByDay[day.number] || [];
                                            const dayTotal = dayItems.reduce((sum, i) => sum + parseFloat(i.total_selling || 0), 0);

                                            return (
                                                <button
                                                    key={day.number}
                                                    className={`qb-day-btn ${activeDay === day.number ? 'active' : ''}`}
                                                    onClick={() => setActiveDay(day.number)}
                                                >
                                                    <div className="qb-day-info">
                                                        <span className="qb-day-num">Day {day.number}</span>
                                                        <span className="qb-day-date">{day.date || 'TBD'}</span>
                                                    </div>
                                                    <div className="qb-day-meta">
                                                        <span className="qb-day-items">{dayItems.length}</span>
                                                        <span className="qb-day-cost">{currency.symbol}{dayTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <button
                                        className="qb-add-day-btn"
                                        onClick={() => toast.info('Add more nights in quotation settings')}
                                    >
                                        <i className="bi bi-plus-lg me-2"></i>
                                        Add Day
                                    </button>
                                </div>
                            </div>

                            {/* Services Area */}
                            <div className={showPreview ? 'col' : 'col-lg-9'}>
                                <div className="qb-services-area">
                                    <div className="qb-services-header">
                                        <h5>
                                            <i className="bi bi-layers me-2"></i>
                                            Day {activeDay} Services
                                        </h5>
                                        <div className="qb-services-actions">
                                            {Object.entries(SERVICE_TYPES).slice(0, 5).map(([type, config]) => (
                                                <button
                                                    key={type}
                                                    className="btn btn-sm btn-outline-secondary"
                                                    style={{ '--hover-color': config.color }}
                                                    onClick={() => {
                                                        setEditingItem({ service_type: type, day_number: activeDay });
                                                        setShowServiceModal(true);
                                                    }}
                                                >
                                                    <i className={`bi ${config.icon}`} style={{ color: config.color }}></i>
                                                </button>
                                            ))}
                                            <div className="dropdown">
                                                <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                                    <i className="bi bi-plus-lg"></i>
                                                </button>
                                                <ul className="dropdown-menu">
                                                    {Object.entries(SERVICE_TYPES).map(([type, config]) => (
                                                        <li key={type}>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    setEditingItem({ service_type: type, day_number: activeDay });
                                                                    setShowServiceModal(true);
                                                                }}
                                                            >
                                                                <i className={`bi ${config.icon} me-2`} style={{ color: config.color }}></i>
                                                                {config.label}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={`qb-services-grid ${viewMode}`}>
                                        <SortableContext
                                            items={(itemsByDay[activeDay] || []).map(i => i.id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <AnimatePresence mode="popLayout">
                                                {(itemsByDay[activeDay] || []).map(item => (
                                                    <SortableServiceItem
                                                        key={item.id}
                                                        item={item}
                                                        onEdit={handleEditService}
                                                        onDelete={handleDeleteService}
                                                        currency={currency}
                                                    />
                                                ))}
                                            </AnimatePresence>
                                        </SortableContext>

                                        {(itemsByDay[activeDay] || []).length === 0 && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="qb-empty-day"
                                            >
                                                <div className="qb-empty-icon">
                                                    <i className="bi bi-calendar2-plus"></i>
                                                </div>
                                                <h5>No services yet</h5>
                                                <p>Add your first service for Day {activeDay}</p>
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={() => handleAddService(activeDay)}
                                                >
                                                    <i className="bi bi-plus-lg me-2"></i>
                                                    Add Service
                                                </button>
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Summary Sidebar */}
                            {showPreview && (
                                <div className="col-lg-3">
                                    <div className="qb-summary-sidebar">
                                        <div className="qb-summary-card">
                                            <h6>
                                                <i className="bi bi-calculator me-2"></i>
                                                Cost Summary
                                            </h6>

                                            <div className="qb-summary-row">
                                                <span>Total Cost</span>
                                                <span>{currency.symbol}{totals.totalCost.toLocaleString('en-IN')}</span>
                                            </div>
                                            <div className="qb-summary-row">
                                                <span>Total Selling</span>
                                                <span className="fw-bold">{currency.symbol}{totals.totalSelling.toLocaleString('en-IN')}</span>
                                            </div>
                                            <div className="qb-summary-row highlight">
                                                <span>Profit</span>
                                                <span className={totals.profit >= 0 ? 'text-success' : 'text-danger'}>
                                                    {currency.symbol}{totals.profit.toLocaleString('en-IN')}
                                                    <small className="ms-1">({totals.profitMargin.toFixed(1)}%)</small>
                                                </span>
                                            </div>
                                        </div>

                                        <div className="qb-summary-card">
                                            <h6>
                                                <i className="bi bi-people me-2"></i>
                                                Per Person
                                            </h6>

                                            {(() => {
                                                const totalPax = (quotation.adults || 0) + (quotation.children || 0);
                                                const perPerson = totalPax > 0 ? totals.totalSelling / totalPax : 0;
                                                return (
                                                    <div className="qb-per-person">
                                                        <span className="qb-pp-value">{currency.symbol}{perPerson.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                                        <span className="qb-pp-label">per person</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="qb-summary-card">
                                            <h6>
                                                <i className="bi bi-building me-2"></i>
                                                Room Config
                                            </h6>
                                            <div className="qb-room-tags">
                                                {Object.entries(roomConfig)
                                                    .filter(([_, v]) => v > 0)
                                                    .map(([k, v]) => (
                                                        <span key={k} className="qb-room-tag">
                                                            {v} {k.toUpperCase()}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>

                                        <div className="qb-summary-card">
                                            <h6>
                                                <i className="bi bi-bar-chart me-2"></i>
                                                By Service Type
                                            </h6>
                                            <div className="qb-service-breakdown">
                                                {chartData.map(item => (
                                                    <div key={item.name} className="qb-breakdown-item">
                                                        <div className="qb-breakdown-bar" style={{ width: `${(item.value / totals.totalCost) * 100}%`, backgroundColor: item.color }}></div>
                                                        <span className="qb-breakdown-label">{item.name}</span>
                                                        <span className="qb-breakdown-value">{currency.symbol}{item.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Drag Overlay */}
                    <DragOverlay>
                        {activeId ? (
                            <div className="service-item dragging-overlay">
                                <div className="service-drag-handle">
                                    <i className="bi bi-grip-vertical"></i>
                                </div>
                                <div className="service-content">
                                    <span>Moving service...</span>
                                </div>
                            </div>
                        ) : null}
                    </DragOverlay>
                </div>

                {/* Service Modal */}
                <AnimatePresence>
                    {showServiceModal && (
                        <ServiceModal
                            show={showServiceModal}
                            onHide={() => {
                                setShowServiceModal(false);
                                setEditingItem(null);
                            }}
                            query={query}
                            quotation={quotation}
                            item={editingItem}
                            dayNumber={activeDay}
                            suppliers={suppliers}
                            destinations={destinations}
                            currencies={currencies}
                            supplierOptions={supplierOptions}
                            destinationOptions={destinationOptions}
                            currency={currency}
                            roomConfig={roomConfig}
                        />
                    )}
                </AnimatePresence>
            </DndContext>

            <style>{`
                .qb-edit-page {
                    min-height: 100vh;
                    background: #f8fafc;
                }

                .qb-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 1.5rem;
                    background: #fff;
                    border-bottom: 1px solid #e2e8f0;
                    position: sticky;
                    top: 0;
                    z-index: 100;
                }

                .qb-header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }

                .qb-back-btn {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f1f5f9;
                    border-radius: 8px;
                    color: #64748b;
                    transition: all 0.2s;
                }

                .qb-back-btn:hover {
                    background: #e2e8f0;
                    color: #334155;
                }

                .qb-title {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .qb-subtitle {
                    font-size: 0.8125rem;
                    color: #64748b;
                    margin-top: 0.25rem;
                }

                .qb-shortcuts-bar {
                    display: flex;
                    gap: 1.5rem;
                    padding: 0.5rem 1.5rem;
                    background: #f1f5f9;
                    border-bottom: 1px solid #e2e8f0;
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .qb-shortcuts-bar kbd {
                    background: #fff;
                    padding: 0.125rem 0.375rem;
                    border-radius: 4px;
                    border: 1px solid #e2e8f0;
                    font-size: 0.6875rem;
                    margin: 0 0.125rem;
                }

                .qb-charts-section {
                    padding: 1.5rem;
                    background: #fff;
                    border-bottom: 1px solid #e2e8f0;
                    overflow: hidden;
                }

                .chart-card {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1rem;
                }

                .chart-card h6 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                }

                .qb-content {
                    padding: 1.5rem;
                }

                .qb-days-nav {
                    width: 200px;
                    background: #fff;
                    border-radius: 12px;
                    padding: 1rem;
                    position: sticky;
                    top: 80px;
                }

                .qb-days-title {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 1rem;
                }

                .qb-days-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .qb-day-btn {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border: 2px solid transparent;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-align: left;
                }

                .qb-day-btn:hover {
                    background: #f1f5f9;
                }

                .qb-day-btn.active {
                    background: #6366f1;
                    color: #fff;
                }

                .qb-day-num {
                    font-weight: 600;
                    font-size: 0.875rem;
                    display: block;
                }

                .qb-day-date {
                    font-size: 0.6875rem;
                    opacity: 0.7;
                }

                .qb-day-meta {
                    text-align: right;
                }

                .qb-day-items {
                    display: block;
                    font-size: 0.75rem;
                    opacity: 0.7;
                }

                .qb-day-cost {
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                .qb-add-day-btn {
                    width: 100%;
                    padding: 0.75rem;
                    margin-top: 0.5rem;
                    background: transparent;
                    border: 2px dashed #e2e8f0;
                    border-radius: 8px;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .qb-add-day-btn:hover {
                    border-color: #6366f1;
                    color: #6366f1;
                }

                .qb-services-area {
                    background: #fff;
                    border-radius: 12px;
                    padding: 1.5rem;
                    min-height: 400px;
                }

                .qb-services-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .qb-services-header h5 {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }

                .qb-services-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                .qb-services-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .qb-services-grid.grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                }

                .service-item {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 10px;
                    border: 1px solid transparent;
                    transition: all 0.2s;
                }

                .service-item:hover {
                    border-color: #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .service-item.dragging {
                    opacity: 0.5;
                }

                .service-drag-handle {
                    color: #cbd5e1;
                    cursor: grab;
                    padding: 0.25rem;
                }

                .service-drag-handle:active {
                    cursor: grabbing;
                }

                .service-icon {
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    color: #fff;
                    font-size: 1.125rem;
                    flex-shrink: 0;
                }

                .service-content {
                    flex: 1;
                    min-width: 0;
                }

                .service-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 0.25rem;
                }

                .service-name {
                    font-size: 0.9375rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .service-badge {
                    font-size: 0.6875rem;
                    padding: 0.125rem 0.5rem;
                    border-radius: 50px;
                    font-weight: 500;
                }

                .service-meta {
                    font-size: 0.75rem;
                    color: #64748b;
                    display: flex;
                    gap: 1rem;
                }

                .service-pricing {
                    display: flex;
                    gap: 1rem;
                    margin-top: 0.5rem;
                    font-size: 0.8125rem;
                }

                .service-cost {
                    color: #64748b;
                }

                .service-sell {
                    color: #1e293b;
                    font-weight: 600;
                }

                .service-actions {
                    display: flex;
                    gap: 0.25rem;
                    opacity: 0;
                    transition: opacity 0.2s;
                }

                .service-item:hover .service-actions {
                    opacity: 1;
                }

                .qb-empty-day {
                    text-align: center;
                    padding: 3rem;
                }

                .qb-empty-icon {
                    width: 80px;
                    height: 80px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f1f5f9;
                    border-radius: 20px;
                    margin: 0 auto 1.5rem;
                    font-size: 2rem;
                    color: #94a3b8;
                }

                .qb-empty-day h5 {
                    color: #1e293b;
                    margin-bottom: 0.5rem;
                }

                .qb-empty-day p {
                    color: #64748b;
                    margin-bottom: 1.5rem;
                }

                .qb-summary-sidebar {
                    position: sticky;
                    top: 80px;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .qb-summary-card {
                    background: #fff;
                    border-radius: 12px;
                    padding: 1rem;
                }

                .qb-summary-card h6 {
                    font-size: 0.8125rem;
                    font-weight: 600;
                    color: #64748b;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .qb-summary-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.5rem 0;
                    font-size: 0.8125rem;
                    color: #64748b;
                }

                .qb-summary-row.highlight {
                    padding-top: 0.75rem;
                    margin-top: 0.5rem;
                    border-top: 1px solid #e2e8f0;
                    font-weight: 600;
                }

                .qb-per-person {
                    text-align: center;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 8px;
                }

                .qb-pp-value {
                    display: block;
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #6366f1;
                }

                .qb-pp-label {
                    font-size: 0.75rem;
                    color: #64748b;
                }

                .qb-room-tags {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0.5rem;
                }

                .qb-room-tag {
                    padding: 0.25rem 0.75rem;
                    background: #f1f5f9;
                    border-radius: 50px;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: #475569;
                }

                .qb-service-breakdown {
                    display: flex;
                    flex-direction: column;
                    gap: 0.75rem;
                }

                .qb-breakdown-item {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 0.75rem;
                }

                .qb-breakdown-bar {
                    position: absolute;
                    left: 0;
                    top: 0;
                    bottom: 0;
                    border-radius: 4px;
                    opacity: 0.15;
                }

                .qb-breakdown-label {
                    position: relative;
                    color: #475569;
                }

                .qb-breakdown-value {
                    position: relative;
                    font-weight: 600;
                    color: #1e293b;
                }

                .dragging-overlay {
                    background: #fff;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    opacity: 0.9;
                }

                @media (max-width: 992px) {
                    .qb-header {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .qb-days-nav {
                        width: 100%;
                        position: relative;
                        top: 0;
                    }

                    .qb-days-list {
                        flex-direction: row;
                        overflow-x: auto;
                    }

                    .qb-day-btn {
                        flex-shrink: 0;
                        min-width: 120px;
                    }

                    .qb-shortcuts-bar {
                        display: none;
                    }
                }
            `}</style>
        </AppLayout>
    );
}

// Service Modal Component
function ServiceModal({
    show,
    onHide,
    query,
    quotation,
    item,
    dayNumber,
    suppliers,
    destinations,
    currencies,
    supplierOptions,
    destinationOptions,
    currency,
    roomConfig,
}) {
    const isEditing = item?.id;

    const { data, setData, post, put, processing, errors, reset } = useForm({
        day_number: item?.day_number || dayNumber || 1,
        service_type: item?.service_type || 'hotel',
        supplier_id: item?.supplier_id || '',
        destination_id: item?.destination_id || '',
        name: item?.name || '',
        description: item?.description || '',
        from_date: item?.from_date ? new Date(item.from_date) : null,
        to_date: item?.to_date ? new Date(item.to_date) : null,
        nights: item?.nights || 1,
        currency_id: item?.currency_id || quotation.currency_id || '',
        cost_single: item?.cost_single || '',
        cost_double: item?.cost_double || '',
        cost_triple: item?.cost_triple || '',
        cost_quad: item?.cost_quad || '',
        cost_extra_bed: item?.cost_extra_bed || '',
        cost_cwb: item?.cost_cwb || '',
        cost_cnb: item?.cost_cnb || '',
        cost_infant: item?.cost_infant || '',
        cost_adult: item?.cost_adult || '',
        cost_child: item?.cost_child || '',
        cost_vehicle: item?.cost_vehicle || '',
        quantity: item?.quantity || 1,
        markup: item?.markup || '',
        discount: item?.discount || '',
        taxes: item?.taxes || '',
        room_type: item?.room_type || '',
        meal_plan: item?.meal_plan || '',
        hotel_option: item?.hotel_option || 'A',
        vehicle_type: item?.vehicle_type || '',
        pickup_location: item?.pickup_location || '',
        dropoff_location: item?.dropoff_location || '',
        pickup_time: item?.pickup_time || '',
        is_optional: item?.is_optional || false,
        remarks: item?.remarks || '',
    });

    // Filter suppliers by service type
    const filteredSupplierOptions = useMemo(() => {
        return supplierOptions.filter(s => {
            if (!s.serviceTypes || s.serviceTypes.length === 0) return true;
            return s.serviceTypes.includes(data.service_type);
        });
    }, [supplierOptions, data.service_type]);

    // Calculate costs
    const calculatedCosts = useMemo(() => {
        let totalCost = 0;
        const rc = roomConfig || {};

        if (data.service_type === 'hotel') {
            const nights = parseInt(data.nights) || 1;
            totalCost = (
                (parseFloat(data.cost_single) || 0) * (rc.single || 0) +
                (parseFloat(data.cost_double) || 0) * (rc.double || 0) +
                (parseFloat(data.cost_triple) || 0) * (rc.triple || 0) +
                (parseFloat(data.cost_quad) || 0) * (rc.quad || 0) +
                (parseFloat(data.cost_extra_bed) || 0) * (rc.extra_bed || 0) +
                (parseFloat(data.cost_cwb) || 0) * (rc.cwb || 0) +
                (parseFloat(data.cost_cnb) || 0) * (rc.cnb || 0) +
                (parseFloat(data.cost_infant) || 0) * (rc.infant || 0)
            ) * nights;
        } else if (data.service_type === 'transfer') {
            totalCost = (parseFloat(data.cost_vehicle) || 0) * (parseInt(data.quantity) || 1);
        } else {
            const adults = quotation.adults || 0;
            const children = quotation.children || 0;
            totalCost = (
                (parseFloat(data.cost_adult) || 0) * adults +
                (parseFloat(data.cost_child) || 0) * children +
                (parseFloat(data.cost_vehicle) || 0)
            ) * (parseInt(data.quantity) || 1);
        }

        const markupAmount = totalCost * (parseFloat(data.markup) || 0) / 100;
        const discountAmount = parseFloat(data.discount) || 0;
        const taxAmount = parseFloat(data.taxes) || 0;
        const totalSelling = totalCost + markupAmount - discountAmount + taxAmount;

        return { totalCost, markup: markupAmount, discount: discountAmount, taxes: taxAmount, totalSelling };
    }, [data, roomConfig, quotation]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = {
            ...data,
            from_date: data.from_date?.toISOString().split('T')[0],
            to_date: data.to_date?.toISOString().split('T')[0],
            total_cost: calculatedCosts.totalCost,
            total_selling: calculatedCosts.totalSelling,
        };

        const url = isEditing
            ? `/queries/${query.id}/quotations/${quotation.id}/items/${item.id}`
            : `/queries/${query.id}/quotations/${quotation.id}/items`;

        if (isEditing) {
            put(url, {
                data: formData,
                preserveScroll: true,
                onSuccess: () => {
                    toast.success('Service updated');
                    onHide();
                },
            });
        } else {
            post(url, {
                data: formData,
                preserveScroll: true,
                onSuccess: () => {
                    toast.success('Service added');
                    onHide();
                },
            });
        }
    };

    const serviceConfig = SERVICE_TYPES[data.service_type] || SERVICE_TYPES.other;

    if (!show) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="service-modal-backdrop"
            onClick={onHide}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="service-modal"
                onClick={e => e.stopPropagation()}
            >
                <div className="service-modal-header">
                    <div className="service-modal-icon" style={{ backgroundColor: serviceConfig.color }}>
                        <i className={`bi ${serviceConfig.icon}`}></i>
                    </div>
                    <div>
                        <h5>{isEditing ? 'Edit Service' : 'Add Service'}</h5>
                        <span className="text-muted">Day {data.day_number}</span>
                    </div>
                    <button className="btn-close ms-auto" onClick={onHide}></button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="service-modal-body">
                        {/* Service Type Tabs */}
                        <div className="service-type-tabs">
                            {Object.entries(SERVICE_TYPES).map(([type, config]) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`service-type-tab ${data.service_type === type ? 'active' : ''}`}
                                    style={{ '--tab-color': config.color }}
                                    onClick={() => setData('service_type', type)}
                                >
                                    <i className={`bi ${config.icon}`}></i>
                                    <span>{config.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="row g-4 mt-3">
                            {/* Left Column - Details */}
                            <div className="col-lg-8">
                                <div className="form-section">
                                    <h6 className="form-section-title">Basic Details</h6>

                                    <div className="row g-3">
                                        <div className="col-md-2">
                                            <label className="form-label">Day</label>
                                            <input
                                                type="number"
                                                min="1"
                                                className="form-control"
                                                value={data.day_number}
                                                onChange={e => setData('day_number', e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-10">
                                            <label className="form-label">Service Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                value={data.name}
                                                onChange={e => setData('name', e.target.value)}
                                                placeholder="e.g., JW Marriott Downtown"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={data.description}
                                                onChange={e => setData('description', e.target.value)}
                                                placeholder="Additional details..."
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Supplier</label>
                                            <Select
                                                options={filteredSupplierOptions}
                                                value={filteredSupplierOptions.find(s => s.value === data.supplier_id)}
                                                onChange={option => setData('supplier_id', option?.value || '')}
                                                isClearable
                                                placeholder="Select supplier..."
                                                classNamePrefix="react-select"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Destination</label>
                                            <Select
                                                options={destinationOptions}
                                                value={destinationOptions.find(d => d.value === data.destination_id)}
                                                onChange={option => setData('destination_id', option?.value || '')}
                                                isClearable
                                                placeholder="Select destination..."
                                                classNamePrefix="react-select"
                                                formatOptionLabel={option => (
                                                    <div>
                                                        <span>{option.label}</span>
                                                        {option.country && <small className="text-muted ms-2">{option.country}</small>}
                                                    </div>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Hotel-specific fields */}
                                {data.service_type === 'hotel' && (
                                    <div className="form-section">
                                        <h6 className="form-section-title">Hotel Details</h6>
                                        <div className="row g-3">
                                            <div className="col-md-3">
                                                <label className="form-label">Check-in</label>
                                                <DatePicker
                                                    selected={data.from_date}
                                                    onChange={date => setData('from_date', date)}
                                                    className="form-control"
                                                    dateFormat="dd MMM yyyy"
                                                    placeholderText="Select date"
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Check-out</label>
                                                <DatePicker
                                                    selected={data.to_date}
                                                    onChange={date => setData('to_date', date)}
                                                    className="form-control"
                                                    dateFormat="dd MMM yyyy"
                                                    minDate={data.from_date}
                                                    placeholderText="Select date"
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Nights</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control"
                                                    value={data.nights}
                                                    onChange={e => setData('nights', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Meal Plan</label>
                                                <select
                                                    className="form-select"
                                                    value={data.meal_plan}
                                                    onChange={e => setData('meal_plan', e.target.value)}
                                                >
                                                    <option value="">Select</option>
                                                    {Object.entries(MEAL_PLANS).map(([key, label]) => (
                                                        <option key={key} value={key}>{key.toUpperCase()}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label">Option</label>
                                                <select
                                                    className="form-select"
                                                    value={data.hotel_option}
                                                    onChange={e => setData('hotel_option', e.target.value)}
                                                >
                                                    <option value="A">A</option>
                                                    <option value="B">B</option>
                                                    <option value="C">C</option>
                                                </select>
                                            </div>
                                        </div>

                                        <h6 className="form-section-subtitle mt-4">Room-wise Cost (per night)</h6>
                                        <div className="room-pricing-grid">
                                            {ROOM_TYPES.map(room => (
                                                <div key={room.key} className="room-pricing-item">
                                                    <label className="form-label small">
                                                        {room.label}
                                                        <span className="badge bg-light text-dark ms-1">
                                                            {roomConfig[room.key] || 0}
                                                        </span>
                                                    </label>
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text">{currency.symbol}</span>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={data[`cost_${room.key}`]}
                                                            onChange={e => setData(`cost_${room.key}`, e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Transfer fields */}
                                {data.service_type === 'transfer' && (
                                    <div className="form-section">
                                        <h6 className="form-section-title">Transfer Details</h6>
                                        <div className="row g-3">
                                            <div className="col-md-5">
                                                <label className="form-label">From</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={data.pickup_location}
                                                    onChange={e => setData('pickup_location', e.target.value)}
                                                    placeholder="Airport, Hotel..."
                                                />
                                            </div>
                                            <div className="col-md-2 d-flex align-items-end justify-content-center">
                                                <i className="bi bi-arrow-right fs-4 text-muted mb-2"></i>
                                            </div>
                                            <div className="col-md-5">
                                                <label className="form-label">To</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={data.dropoff_location}
                                                    onChange={e => setData('dropoff_location', e.target.value)}
                                                    placeholder="Hotel, Port..."
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Vehicle Type</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={data.vehicle_type}
                                                    onChange={e => setData('vehicle_type', e.target.value)}
                                                    placeholder="Sedan, SUV..."
                                                />
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Cost/Vehicle</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">{currency.symbol}</span>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={data.cost_vehicle}
                                                        onChange={e => setData('cost_vehicle', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Qty</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control"
                                                    value={data.quantity}
                                                    onChange={e => setData('quantity', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Per-person pricing */}
                                {['sightseeing', 'meal', 'visa', 'insurance', 'guide', 'flight', 'cruise', 'other'].includes(data.service_type) && (
                                    <div className="form-section">
                                        <h6 className="form-section-title">Pricing</h6>
                                        <div className="row g-3">
                                            <div className="col-md-3">
                                                <label className="form-label">Per Adult</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">{currency.symbol}</span>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={data.cost_adult}
                                                        onChange={e => setData('cost_adult', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Per Child</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">{currency.symbol}</span>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={data.cost_child}
                                                        onChange={e => setData('cost_child', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Vehicle (if any)</label>
                                                <div className="input-group">
                                                    <span className="input-group-text">{currency.symbol}</span>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={data.cost_vehicle}
                                                        onChange={e => setData('cost_vehicle', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Quantity</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control"
                                                    value={data.quantity}
                                                    onChange={e => setData('quantity', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column - Cost Summary */}
                            <div className="col-lg-4">
                                <div className="cost-summary-panel">
                                    <h6>Cost Summary</h6>

                                    <div className="cost-row">
                                        <span>Base Cost</span>
                                        <span>{currency.symbol}{calculatedCosts.totalCost.toLocaleString('en-IN')}</span>
                                    </div>

                                    <div className="cost-row">
                                        <div className="cost-input-group">
                                            <span>Markup</span>
                                            <input
                                                type="number"
                                                value={data.markup}
                                                onChange={e => setData('markup', e.target.value)}
                                                placeholder="0"
                                            />
                                            <span>%</span>
                                        </div>
                                        <span className="text-success">+{currency.symbol}{calculatedCosts.markup.toLocaleString('en-IN')}</span>
                                    </div>

                                    <div className="cost-row">
                                        <div className="cost-input-group">
                                            <span>Discount</span>
                                            <input
                                                type="number"
                                                value={data.discount}
                                                onChange={e => setData('discount', e.target.value)}
                                                placeholder="0"
                                            />
                                        </div>
                                        <span className="text-danger">-{currency.symbol}{calculatedCosts.discount.toLocaleString('en-IN')}</span>
                                    </div>

                                    <div className="cost-row">
                                        <div className="cost-input-group">
                                            <span>Taxes</span>
                                            <input
                                                type="number"
                                                value={data.taxes}
                                                onChange={e => setData('taxes', e.target.value)}
                                                placeholder="0"
                                            />
                                        </div>
                                        <span>+{currency.symbol}{calculatedCosts.taxes.toLocaleString('en-IN')}</span>
                                    </div>

                                    <div className="cost-total">
                                        <span>Selling Price</span>
                                        <span>{currency.symbol}{calculatedCosts.totalSelling.toLocaleString('en-IN')}</span>
                                    </div>

                                    {data.service_type === 'hotel' && Object.keys(roomConfig).some(k => roomConfig[k] > 0) && (
                                        <div className="room-config-ref">
                                            <h6>Room Config</h6>
                                            <div className="room-tags">
                                                {Object.entries(roomConfig)
                                                    .filter(([_, v]) => v > 0)
                                                    .map(([k, v]) => (
                                                        <span key={k} className="room-tag">{v} {k.toUpperCase()}</span>
                                                    ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-check mt-3">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="is_optional"
                                            checked={data.is_optional}
                                            onChange={e => setData('is_optional', e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="is_optional">
                                            Mark as Optional
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="service-modal-footer">
                        <button type="button" className="btn btn-outline-secondary" onClick={onHide}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={processing}>
                            {processing ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className={`bi ${isEditing ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                            )}
                            {isEditing ? 'Update' : 'Add'} Service
                        </button>
                    </div>
                </form>
            </motion.div>

            <style>{`
                .service-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1050;
                    padding: 1rem;
                }

                .service-modal {
                    background: #fff;
                    border-radius: 16px;
                    width: 100%;
                    max-width: 1000px;
                    max-height: 90vh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .service-modal-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    padding: 1.25rem 1.5rem;
                    border-bottom: 1px solid #f1f5f9;
                }

                .service-modal-icon {
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 12px;
                    color: #fff;
                    font-size: 1.5rem;
                }

                .service-modal-header h5 {
                    margin: 0;
                    font-size: 1.125rem;
                    font-weight: 600;
                }

                .service-modal-body {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1.5rem;
                }

                .service-type-tabs {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }

                .service-type-tab {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #f8fafc;
                    border: 2px solid transparent;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.8125rem;
                }

                .service-type-tab:hover {
                    background: #f1f5f9;
                }

                .service-type-tab.active {
                    border-color: var(--tab-color);
                    background: color-mix(in srgb, var(--tab-color) 10%, white);
                }

                .service-type-tab i {
                    color: var(--tab-color);
                }

                .form-section {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.25rem;
                    margin-bottom: 1rem;
                }

                .form-section-title {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                }

                .form-section-subtitle {
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: #64748b;
                    margin-bottom: 0.75rem;
                }

                .room-pricing-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 0.75rem;
                }

                .room-pricing-item label {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .cost-summary-panel {
                    background: #f8fafc;
                    border-radius: 12px;
                    padding: 1.25rem;
                    position: sticky;
                    top: 0;
                }

                .cost-summary-panel h6 {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin-bottom: 1rem;
                    padding-bottom: 0.75rem;
                    border-bottom: 1px solid #e2e8f0;
                }

                .cost-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.5rem 0;
                    font-size: 0.8125rem;
                }

                .cost-input-group {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .cost-input-group input {
                    width: 60px;
                    padding: 0.25rem 0.5rem;
                    border: 1px solid #e2e8f0;
                    border-radius: 4px;
                    font-size: 0.8125rem;
                    text-align: right;
                }

                .cost-total {
                    display: flex;
                    justify-content: space-between;
                    padding: 1rem 0 0;
                    margin-top: 0.5rem;
                    border-top: 2px solid #e2e8f0;
                    font-size: 1.125rem;
                    font-weight: 700;
                    color: #1e293b;
                }

                .room-config-ref {
                    margin-top: 1.5rem;
                    padding-top: 1rem;
                    border-top: 1px solid #e2e8f0;
                }

                .room-config-ref h6 {
                    font-size: 0.75rem;
                    color: #94a3b8;
                    margin-bottom: 0.75rem;
                    padding-bottom: 0;
                    border-bottom: none;
                }

                .room-tags {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }

                .room-tag {
                    padding: 0.25rem 0.5rem;
                    background: #fff;
                    border-radius: 4px;
                    font-size: 0.6875rem;
                    font-weight: 600;
                    color: #6366f1;
                }

                .service-modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 0.75rem;
                    padding: 1rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                }

                /* React Select custom styles */
                .react-select__control {
                    border-color: #e2e8f0 !important;
                    border-radius: 6px !important;
                }

                .react-select__control--is-focused {
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 1px #6366f1 !important;
                }

                .react-select__menu {
                    z-index: 100 !important;
                }

                /* React DatePicker custom styles */
                .react-datepicker-wrapper {
                    width: 100%;
                }

                .react-datepicker__input-container input {
                    width: 100%;
                }

                @media (max-width: 768px) {
                    .room-pricing-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }
            `}</style>
        </motion.div>
    );
}
